import type { KalturaMediaAsset } from '@kaltura-ott/ott-client-types';
import { KalturaCategoryTree } from '@kaltura-ott/ott-client-types/api/types';
import {
  addFullPersonalList,
  cacheToPersist,
  CachingServiceImpl,
  deleteAllPersonalList,
  FAKE_TTL,
  getDms,
  KuxCategoriesDTO,
  KuxDms,
  kuxSetDMS,
  LS_KEY_DEBUG,
  LS_KEY_ENDPOINT,
  LS_KEY_PARTNER_ID,
  setImageScale,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
  upgradeWindowDates,
  DEFAULT_PLAYABLE_CATCHUP_BUFFER,
  getRatiosByImageTypes,
  PresentationAdditionalTypeEnum,
  BitrateLimitationMode,
  getLogoAsset,
  getBase64ImageFromUrl,
  setCacheValue,
  getCacheValue,
  AssetPresentationRatioEnum,
  adjustFilteredCategoryTree,
  getAssetsByChannelId,
  PromotionScreenOptions,
  assetToType,
  categoryItemsIdList,
  setUidaToCategoryTree,
  PREDEFIENED_REFERENCES,
  getNewFilteredCategoryTree,
  KuxRating,
} from '@kaltura-ott/tvpil';
import { addMiddleware } from '@kaltura-ott/tvpil';
import {
  createAppServiceEvent,
  createConfigurationEvent,
  createErrorEvent,
  tvpilJournal,
} from '@kaltura-ott/tvpil-utils';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { offlineLauncherData } from '../../data/offline/launcher';
import { createRenewKsMiddleware } from '../../middlewares/renew-ks';
import { fetchCategoryTree, fetchUidas } from '../../rhino/data';
import { queryClient } from '../../rhino/data/queryClient';
import { queryKeys } from '../../rhino/data/queryKeys';
import { FEATURE_ENABLER_VARIATIONS, featureEnabler } from '../../utils';
import type { EpgStore, HeroRailStoreV2, PageStore } from '../category';
import { CategoryChildren } from '../category/category-children';
import { RootStore } from '../category/root/root-store';
import { FavoritesStore } from '../category/subpage/favorites';
import type { LastWatchedLinearStore } from '../channelStats/last-watched-linear-store';
import { ConcurrencyStore } from '../concurrency/concurrency-store';
import { CookiesStore } from '../cookie';
import { DebugMenuStore } from '../debugMenu';
import { DmsStore } from '../dms';
import { creatorEntityStore } from '../entities/entities.service';
import { LinkAsAsset } from '../entities/LinkAsAsset';
import type { FavoriteRepository } from '../favoriteRepository';
import { KeyboardStore } from '../keyboard';
import { LocalizationStore } from '../localization';
import type { UserStore } from '../user';
import type { LoginStore } from '../user/login-store';

import { TopShelfStore } from './../topShelf/top-shelf-store';
import { AppParams } from './app-params';

enum TemplatesKeys {
  PLAYABLE_CATCHUP_BUFFER_TIME = 'PLAYABLE_CATCHUP_BUFFER_TIME',
}

export type Templates = { [key in TemplatesKeys]: () => string };

export const CUSTOMER_LOGO_CACHE_KEY = 'customerLogo';

const templates: Templates = {
  // [opc template string]: [dms property]
  PLAYABLE_CATCHUP_BUFFER_TIME: () => {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    return dmsStore.dms?.playableCatchupBuffer || DEFAULT_PLAYABLE_CATCHUP_BUFFER;
  },
};

/**
 * Filters category tree and initializes root store
 *
 * @param categoryTree
 */
export const createRootStore = (categoryTree: KuxCategoriesDTO) => {
  const parsedCategoryTree = getNewFilteredCategoryTree({
    categoryTree: [categoryTree],
    templates,
    predefinedKSQL: featureEnabler.variation(FEATURE_ENABLER_VARIATIONS.predefinedKsql, ''),
  })[0];
  adjustFilteredCategoryTree(parsedCategoryTree);

  const rootStore = new RootStore(parsedCategoryTree);
  const topShelfStore = new TopShelfStore(parsedCategoryTree);

  const lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
  lastWatchedLinear.setMainHeroRail(findHeroStoreOnPage(PREDEFIENED_REFERENCES.home, rootStore));

  return { rootStore, topShelfStore };
};

/**
 * Find recursively all stores that can be cached
 *
 * @param children
 * @param subTypes
 * @param types
 */
export const findPreloadStores = (
  children?: CategoryChildren[],
  subTypes: (UIDAPresentationTypeEnum | PresentationAdditionalTypeEnum)[] = [],
  types: UIDAStructureTypeEnum[] = [],
) => {
  let stores: CategoryChildren[] = [];
  if (children && children.length) {
    children.forEach((child) => {
      if (!subTypes.length && !types.length) {
        stores.push(child);
      }
      if (subTypes.includes(child.subtype) || types.includes(child.type)) {
        stores.push(child);
      }
      stores = stores.concat(findPreloadStores(child.children, subTypes, types));
    });
  }
  return stores;
};

/**
 * Find recursively all stores that can be cached
 *
 * @param children
 * @param subTypes
 * @param types
 */
export const findCachedStores = (
  children?: CategoryChildren[],
  subTypes: UIDAPresentationTypeEnum[] = [UIDAPresentationTypeEnum.epg],
  types: UIDAStructureTypeEnum[] = [UIDAStructureTypeEnum.widget],
) => {
  let stores = findPreloadStores(children, subTypes, types);
  if (children && children.length) {
    children.forEach((child) => {
      stores = stores.concat(findPreloadStores(child.children, subTypes, types));
    });
  }
  return stores;
};

/**
 * @param recommendationFields
 * @param savedInterestItemsList
 * @param typeArr
 * @param suggestedCalculationByLastXItems
 */
export const suggestedRecalculation = async (
  recommendationFields: string[],
  savedInterestItemsList: number,
  typeArr: string[],
  suggestedCalculationByLastXItems: number,
) => {
  await lastValueFrom(deleteAllPersonalList({ partnerListId: savedInterestItemsList }));
  await lastValueFrom(
    addFullPersonalList({
      partnerListId: savedInterestItemsList,
      recommendationFields,
      typeArr,
      suggestedCalculationByLastXItems,
    }),
  );
};

/**
 * Initializes epg and hero rail stores
 * @param preloadStores
 * @param sizeIndex
 */
export const preload = async (preloadStores: (EpgStore | HeroRailStoreV2)[]) => {
  let epgStoresV2: EpgStore[] = [];

  epgStoresV2 = preloadStores.filter((a) => a.subtype === UIDAPresentationTypeEnum.epg) as EpgStore[];

  const heroStores = preloadStores.filter((a) => a.subtype === UIDAPresentationTypeEnum.hero) as HeroRailStoreV2[];

  const promises = [
    ...epgStoresV2.map((store) => store.init({}, true)),
    ...heroStores.map((store) => {
      if (!store.isReady && !store.isInitializing) {
        return store.init(true);
      }
    }),
  ];

  for (let i = 0; i < promises.length; i++) {
    await promises[i];
  }
};

/**
 *
 * @param epgStores
 * @param timeout
 */
export const dailyCaching = (epgStores: EpgStore[], timeout: number) => {
  // setTimeout(async () => {
  //   for (const epgStore of epgStores) {
  //     await getScreenEpg({
  //       channelId: epgStore.currentFilter.id,
  //       screenLength: EpgStoreV2.screenLength,
  //       deltaDays: 1,
  //       channelsStats: channelStats.getMostWatched(
  //         epgStore.mostWatchedParams.presentMostWatchedPeriod,
  //         epgStore.mostWatchedParams.presentMostWatchedCount,
  //       ),
  //       orderBatches: epgStore.orderBatches,
  //     }).toPromise();
  //   }
  //   dailyCaching(epgStores, timeout + DAYLY_CACHING_INTERVAL);
  // }, timeout);
};

export const findHeroStores = (rootStore: RootStore) => {
  const preloadStores: CategoryChildren[] = findPreloadStores(rootStore.children, [UIDAPresentationTypeEnum.hero]);
  return preloadStores.filter((a) => a.subtype === UIDAPresentationTypeEnum.hero) as HeroRailStoreV2[];
};

export const findHeroStoreOnPage = (pageReferenceId: string, rootStore: RootStore) => {
  const page = rootStore.children.find((page) => page.referenceId === pageReferenceId);
  if (page) {
    return page.children.find((widget) => widget.subtype === UIDAPresentationTypeEnum.hero) as HeroRailStoreV2;
  }
};

/**
 * Retrieves dms or populates offline dms data
 * @param appParams
 */
export const setupDMS = async (appParams: AppParams) => {
  let dms: KuxDms;
  if (appParams.isOffline) {
    dms = {
      analytics: {
        externalKey: '',
        plugins: [],
      },
      bingeWatching: {
        enableNextEpisode: true,
        enableBingeWatchingUi: true,
        enableAutomaticNextEpisode: true,
        automaticNextEpisodeStartTime: 7,
        nextEpisodeMaxCountdownTime: 7,
        skipIntroMaxCountdownTime: 7,
        skipRecapMaxCountdownTime: 7,
      },
      bitrateLimitationManagement: {
        bitrateLimitSetBy: BitrateLimitationMode.user,
        userAllowedTo: false,
        localVqSettings: {},
      },
      serverTimeOut: 10,
      forcedUpdateURLObject: {},
      endpointUrl: '',
      cloudFrontUrl: '',
      graphQlUrl: '',
      apiVersion: '5.4.0',
      rootCategoryID: 0,
      consecutiveRequestFailuresLimit: 2,
      filesFormat: {
        LIVE: ['STB_Devices_Main_DASH', 'mpegdash'],
        TRAILER: ['Trailer', 'applehttp'],
        VOD: ['WEB_Devices_Main_DASH', 'mpegdash'],
        WELCOME_POPUP: ['WEB_Devices_Main_DASH', 'mpegdash'],
        LIVE_HEVC: ['WEB_Devices_Main_DASH', 'mpegdash'],
        VOD_HEVC: ['WEB_Devices_Main_DASH', 'mpegdash'],
      },
      mediaTypes: {
        Episode: 'Episode',
        Movie: 'Movie',
        Series: 'Series',
        Linear: 'Linear',
        Program: 'Program',
        Season: 'Season',
        Package: 'Package',
        Person: 'Person',
        Logo: 'Logo',
        in_application_link_asset: 'in_application_link_asset',
      },
      mediaTypesValue: {},
      translationLinks: {},
      launchDarkly: {},
      bgVideo: 0,
      companyLogoUrl: '',
      loadingAnimationUrl: '',
      loadingBgColor: '#fff',
      suggestedCalculationByLastXItems: 0,
      suggestedRecalculationPeriod: 0,
      wrongPinTriesLimit: 3,
      wrongPinTimeout: 120,
      playableCatchupBuffer: DEFAULT_PLAYABLE_CATCHUP_BUFFER,
      temporaryParams: {
        endVoiceSearchTimeInSec: 3,
        subscriptionDenialList: [],
        ppvDenialList: [],
        filterLowerSubscription: true,
      },
      multicastParams: {
        HEVCCapable: false,
        WVCASCapable: false,
        multicastEndpoint: '',
        multicastIndicationKey: '',
        multicastPauseResumeABRThreshold: 30,
      },
      mosaicInfo: {
        isMosaicSupported: false,
        mosaicPlayerRandomSecondsTime: 180,
      },
      ratingMap: [],
      ratingNameMap: new Map<string, KuxRating>(),
      silentLoginSettings: {},
      prePaidUserId: 1,
      providers: {
        hbo: {
          activationUrl: '',
          shortActivationUrl: '',
          metaKey: 'ExternalPackageProvider',
          metaValue: 'hbo',
          providerImageUrl: '',
          supportPhone: '',
          iOsStore: '',
          androidTVStore: '',
          androidStore: '',
          lgStore: '',
          tizenStore: '',
          appPackageName: '',
          alternativeIconName: '',
          showPopupBeforePlayback: true,
          displayName: '',
          userDynamicDataCodeKey: '',
        },
      },
      minFFEntitlementCheckIntervalSec: 600,
      epgTvTimelineInMinutes: 210,
      GridCharacteristics: {
        GRID_TYPES: [],
        GROUP_BY_VALUE: 'name',
      },
      searchCharacteristics: {
        GROUP_BY_VALUE: 'name',
      },
      exponea: {
        enable: false,
      },
      livePlaybackBufferFetching: 5 * 60 * 1000,
      showCastAndCrewInSearch: true,
      gdpr: {},
      promoScreen: {
        options: {},
      },
      jwt: {},
    };
  } else {
    dms = await lastValueFrom(getDms({ ...appParams.dmsSetting, udid: appParams.udid }));
  }
  kuxSetDMS(dms);
  return dms;
};

const attachUserStoreMiddlewares = (() => {
  // eslint-disable-next-line @typescript-eslint/ban-types
  const attachedMiddlewares: Function[] = [];
  return (loginStore: LoginStore) => {
    // attached only once
    if (attachedMiddlewares.length) {
      return;
    }

    // add new ones
    attachedMiddlewares.push(addMiddleware(createRenewKsMiddleware(loginStore)));
  };
})();

// timestamp of last download categoryTree
export let lastFetchCategoryTreeTimestamp = 0;
/**
 *
 * @param dms
 * @param appParams
 * @param cacheId - last categoryTree id. If the same with new - skip download
 */
export const createCategoryTree = async (
  dms: KuxDms,
  appParams: AppParams,
  cacheId?: number,
): Promise<{
  rootStore: RootStore;
  categoryTree: KuxCategoriesDTO;
  topShelfStore: TopShelfStore;
} | void> => {
  const categoryId =
    dms.e2eRootCategoryID ||
    DebugMenuStore.rootCategoryId ||
    featureEnabler.variation('ROOT_CATEGORY_ID') ||
    dms.rootCategoryID;

  const fullKalturaCategoryTree = await queryClient.fetchQuery({
    queryKey: queryKeys.categoryTree,
    queryFn: () => fetchCategoryTree({ categoryId }),
    staleTime: 0,
    gcTime: 0,
  });

  lastFetchCategoryTreeTimestamp = Date.now();

  if (cacheId && cacheId === fullKalturaCategoryTree.id) {
    return undefined;
  }

  if (!fullKalturaCategoryTree) {
    return;
  }

  const categoriesIds = categoryItemsIdList([fullKalturaCategoryTree as unknown as KalturaCategoryTree]);

  const [{ objects: categoriesAssets }] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: queryKeys.categoryTreeUidas(categoriesIds),
      queryFn: () => fetchUidas(categoriesIds),
      staleTime: 0,
      gcTime: 0,
    }),
    lastValueFrom(getRatiosByImageTypes()),
  ]);

  const categoryTree = setUidaToCategoryTree(
    [fullKalturaCategoryTree as unknown as KalturaCategoryTree],
    categoriesAssets as unknown as KalturaMediaAsset[],
  )[0];

  // @ts-expect-error TODO Fix
  const { rootStore, topShelfStore } = createRootStore(categoryTree);

  const lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
  await lastWatchedLinear.setupDefaultChannels(findHeroStores(rootStore));

  // @ts-expect-error TODO Fix
  return { rootStore, categoryTree, topShelfStore };
};

const loadCustomerLogo = async () => {
  try {
    const imageAsset = await lastValueFrom(getLogoAsset());

    if (imageAsset && imageAsset.images[AssetPresentationRatioEnum['1x1']]?.url) {
      const base64Logo = await getBase64ImageFromUrl(
        `${imageAsset.images[AssetPresentationRatioEnum['1x1']]?.url}/width/0/height/0`,
      );
      setCacheValue(CUSTOMER_LOGO_CACHE_KEY, base64Logo);
      return base64Logo;
    }
  } catch (e) {
    tvpilJournal.log(createConfigurationEvent('CustomerLogoMissing'));
  }

  return '';
};

/**
 * Initialize stores
 * @param appParams
 * @param dms
 */
export const bootApp = async (appParams: AppParams, dms: KuxDms, analyticsInitializer?: () => void) => {
  tvpilJournal.log(createAppServiceEvent('BootAppStart'));

  if (appParams.designParams.imageScale) {
    setImageScale(appParams.designParams.imageScale);
  }

  const loginStore = container.resolve<LoginStore>('loginStore');
  loginStore.init();
  attachUserStoreMiddlewares(loginStore);

  const userStore = container.resolve<UserStore>('userStore');
  userStore.init();
  await userStore.setupUser();

  tvpilJournal.log(createAppServiceEvent('UserReady'));

  if (analyticsInitializer) {
    analyticsInitializer();
  }

  await userStore.initDataSavingMode();

  const localizationStore = container.resolve<LocalizationStore>('localizationStore');
  await localizationStore.init();

  tvpilJournal.log(createAppServiceEvent('LocalizationReady'));

  const parsedCategoryData = await createCategoryTree(dms, appParams);

  if (!parsedCategoryData) {
    tvpilJournal.log(createErrorEvent('InvalidCategoryTree'));

    throw new Error('cannot parse category tree');
  }

  tvpilJournal.log(createAppServiceEvent('CategoryTreeReady'));

  const { categoryTree, rootStore, topShelfStore } = parsedCategoryData;

  const favoriteRepository = container.resolve<FavoriteRepository>('favoriteRepository');
  favoriteRepository.init(FavoritesStore.TTL);
  const promises = [];

  if (!loginStore.isAnonymous) {
    promises.push(favoriteRepository.getFavoritesList());
  }

  rootStore.assetDetailsPages?.init(dms);

  const concurrencyStore = container.resolve(ConcurrencyStore);
  concurrencyStore.init();

  const keyboardsStaticPath = featureEnabler.variation('keyboardsStaticPath', '');

  if (!keyboardsStaticPath) {
    tvpilJournal.log(createConfigurationEvent('KeyboardStaticPathMissing'));
  }
  const keyboardStore = container.resolve(KeyboardStore);
  keyboardStore.setUp(`${dms.staticAssetsUrl}${keyboardsStaticPath}`);

  const cookieStore = container.resolve(CookiesStore);
  cookieStore.init();

  promises.push(loadCustomerLogo());
  const [, customerLogo] = await Promise.all(promises);

  tvpilJournal.log(createAppServiceEvent('FavoritesReady'));

  tvpilJournal.log(createAppServiceEvent('CustomerLogoReady'));

  loginStore.isPostLoginProcessing = false;

  tvpilJournal.log(createAppServiceEvent('BootAppEnd'));

  return {
    categoryTree,
    rootStore,
    topShelfStore,
    customerLogo,
  };
};

/**
 * Initializes stores
 * @param appParams
 */
export const bootAppOffline = async (appParams: AppParams) => {
  if (appParams.designParams.imageScale) {
    setImageScale(appParams.designParams.imageScale);
  }
  const userStore = container.resolve<UserStore>('userStore');
  const loginStore = container.resolve<LoginStore>('loginStore');
  attachUserStoreMiddlewares(loginStore);
  await userStore.init();

  const localizationStore = container.resolve<LocalizationStore>('localizationStore');
  await localizationStore.initOffline();

  const { rootStore, topShelfStore } = createRootStore(offlineLauncherData);
  const customerLogo = (await getCacheValue(CUSTOMER_LOGO_CACHE_KEY)) || '';

  return {
    categoryTree: offlineLauncherData,
    rootStore,
    topShelfStore,
    customerLogo,
  };
};

export const preloadAfterLoad = async ({
  dms,
  children,
  subtypes = [UIDAPresentationTypeEnum.epg, UIDAPresentationTypeEnum.hero],
  skipEpgPrograms,
}: {
  dms: KuxDms;
  children?: CategoryChildren[];
  subtypes: (UIDAPresentationTypeEnum | PresentationAdditionalTypeEnum)[];
  skipEpgPrograms?: boolean;
}) => {
  const preloadStores = findPreloadStores(children, subtypes) as (EpgStore | HeroRailStoreV2)[];
  await preload(preloadStores);

  const epgStores = preloadStores.filter(({ subtype }) => subtype === UIDAPresentationTypeEnum.epg) as EpgStore[];

  if (!skipEpgPrograms) {
    for (let i = 0; i < epgStores.length; i++) {
      await epgStores[i].preloadPrograms();
    }
  }

  if (featureEnabler.variation('cachingDailyEPG', false) && dms.upgradeWindowStartTime && dms.upgradeWindowEndTime) {
    const [startDate, endDate] = upgradeWindowDates(dms.upgradeWindowStartTime, dms.upgradeWindowEndTime);
    dailyCaching(epgStores, Math.ceil(Math.random() * (endDate.getTime() - startDate.getTime())));
  }
  if (featureEnabler.variation('enablePersistentEPGCache', true)) {
    cacheToPersist();
  }
};

/**
 *
 * @param children
 * @param subTypes
 * @param types
 */
export const resetWidgets = (
  children: CategoryChildren[],
  subTypes?: UIDAPresentationTypeEnum[],
  types?: UIDAStructureTypeEnum[],
) => {
  const stories = findCachedStores(children, subTypes, types);
  stories.forEach((store) => {
    if ('resetStore' in store) {
      store.resetStore();
    }
  });
};

export const reInitTotalNpvrRail = async (children: CategoryChildren[]) => {
  const nPVRPageStore = findPreloadStores(children, [], [UIDAStructureTypeEnum.page]).find(
    (page) => page.subtype === UIDAPresentationTypeEnum.npvr,
  ) as PageStore;
  if (nPVRPageStore) {
    if ('resetStore' in nPVRPageStore) {
      nPVRPageStore?.totalRecordsRail?.resetStore();
    }
    await nPVRPageStore?.totalRecordsRail?.init();
  }
};

/**
 *
 */
export const reInitWidget = async (
  children: CategoryChildren[],
  subTypes?: UIDAPresentationTypeEnum[],
  types?: UIDAStructureTypeEnum[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filter?: any,
) => {
  let stories = findCachedStores(children, subTypes, types);

  if (filter) {
    stories = stories.filter(filter);
  }

  stories.forEach((store) => {
    if ('resetStore' in store) {
      store.resetStore();
    }
  });

  return Promise.all(
    stories.map((store) => {
      if ('init' in store) {
        store.init({});
      }
    }),
  );
};

/**
 * Storage validation for the current env
 * @param cacheService
 * @param partnerId
 * @param endpoint
 */
export const checkActualCache = async (cacheService: CachingServiceImpl, partnerId: number, endpoint: string) => {
  const oldPartnerId = await cacheService.getItem(LS_KEY_PARTNER_ID);
  const oldEndpoint = await cacheService.getItem(LS_KEY_ENDPOINT);

  if (oldPartnerId && (oldPartnerId !== partnerId || oldEndpoint !== endpoint)) {
    const debugCache = await cacheService.getItem(LS_KEY_DEBUG);
    await cacheService.clear();
    if (debugCache) {
      await cacheService.setItem(LS_KEY_DEBUG, debugCache, FAKE_TTL);
    }
  }

  await cacheService.setItem(LS_KEY_PARTNER_ID, partnerId, FAKE_TTL);
  await cacheService.setItem(LS_KEY_ENDPOINT, endpoint, FAKE_TTL);
};

export interface PromoAssets {
  [key: string]: LinkAsAsset;
}

export const getPromoAssets = async (
  collectionId: number,
  promotionScreen: PromotionScreenOptions,
): Promise<PromoAssets> => {
  const assets = await lastValueFrom(getAssetsByChannelId({ channelId: collectionId }));

  if (!assets) {
    // @ts-expect-error TODO Fix
    return;
  }

  const promoAssetMapping = {
    PromotionUrl: 'promotionAsset',
    PromotionUrlTablet: 'promotionAssetTablet',
    PromotionUrlSmartphone: 'promotionAssetMobile',
  };

  const promoAssets: PromoAssets = {};

  for (const [platformName, id] of Object.entries(promotionScreen)) {
    const promoAsset = assets?.objects.find((asset) => asset.id === id);

    // @ts-expect-error TODO Fix
    if (!promoAssetMapping[platformName]) {
      // @ts-expect-error TODO Fix
      return;
    }
    // @ts-expect-error TODO Fix
    promoAssets[promoAssetMapping[platformName]] = creatorEntityStore(assetToType(promoAsset));
  }

  return promoAssets;
};
